import { apiClient } from './api-client';

export interface TeamMember {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  avatarUrl?: string;
  role: TeamMemberRole;
  status: TeamMemberStatus;
  mfaEnabled: boolean;
  lastLoginAt?: string;
  createdAt: string;
}

export type TeamMemberRole = 'admin' | 'supervisor' | 'agent';
export type TeamMemberStatus = 'active' | 'inactive' | 'pending';

export interface PendingInvitation {
  id: string;
  email: string;
  role: TeamMemberRole;
  firstName?: string;
  lastName?: string;
  expiresAt: string;
}

export interface InviteTeamMemberDto {
  email: string;
  role: TeamMemberRole;
  firstName?: string;
  lastName?: string;
}

export interface UpdateTeamMemberDto {
  role?: TeamMemberRole;
  status?: TeamMemberStatus;
  firstName?: string;
  lastName?: string;
  displayName?: string;
}

export interface TeamMembersResponse {
  data: TeamMember[];
  total: number;
}

/** One person in the organisation directory (readable by every signed-in user). */
export interface DirectoryEntry {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
}

export interface TeamMemberFilters {
  search?: string;
  role?: TeamMemberRole;
  status?: TeamMemberStatus;
  page?: number;
  limit?: number;
}

class TeamMembersService {
  private readonly basePath = '/team-members';

  /**
   * Get all team members with optional filters.
   */
  async getTeamMembers(filters?: TeamMemberFilters): Promise<TeamMembersResponse> {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.role) params.append('role', filters.role);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;

    const response = await apiClient.get<TeamMembersResponse>(url);
    return response.data;
  }

  /**
   * Every active and pending user in the organisation — id, name and email
   * only. Unlike getTeamMembers (admin-only), any signed-in user may call it;
   * it backs @mentions and the reply To/Cc suggestions, and (with activeOnly,
   * which leaves out pending invitations) the ticket assignee pickers.
   */
  async getDirectory(options: { activeOnly?: boolean } = {}): Promise<{ data: DirectoryEntry[]; total: number }> {
    const response = await apiClient.get<{ data: DirectoryEntry[]; total: number }>(
      `${this.basePath}/directory${options.activeOnly ? '?activeOnly=true' : ''}`,
    );
    return response.data;
  }

  /**
   * Get a single team member by ID.
   */
  async getTeamMember(id: string): Promise<TeamMember> {
    const response = await apiClient.get<TeamMember>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Invite a new team member.
   */
  async inviteTeamMember(dto: InviteTeamMemberDto): Promise<{ success: boolean; tokenId: string; message: string }> {
    const response = await apiClient.post<{ success: boolean; tokenId: string; message: string }>(
      `${this.basePath}/invite`,
      dto
    );
    return response.data;
  }

  /**
   * Update a team member.
   */
  async updateTeamMember(id: string, dto: UpdateTeamMemberDto): Promise<TeamMember> {
    const response = await apiClient.put<TeamMember>(`${this.basePath}/${id}`, dto);
    return response.data;
  }

  /**
   * Deactivate a team member.
   */
  async deactivateTeamMember(id: string): Promise<void> {
    await apiClient.post(`${this.basePath}/${id}/deactivate`);
  }

  /**
   * Reactivate a team member.
   */
  async reactivateTeamMember(id: string): Promise<void> {
    await apiClient.post(`${this.basePath}/${id}/reactivate`);
  }

  /**
   * Delete a team member (removes the user from the system).
   * Backend-restricted to platform Super Admins.
   */
  async deleteTeamMember(id: string): Promise<void> {
    await apiClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Count a member's active (non-terminal) assigned tickets. Used to warn and
   * restrict deletion before the action — matches the backend guard exactly.
   */
  async getActiveTicketCount(id: string): Promise<number> {
    const response = await apiClient.get<{ count: number }>(
      `${this.basePath}/${id}/active-ticket-count`,
    );
    return response.data?.count ?? 0;
  }

  /**
   * Get pending invitations.
   */
  async getPendingInvitations(): Promise<PendingInvitation[]> {
    const response = await apiClient.get<PendingInvitation[]>(`${this.basePath}/invitations`);
    return response.data;
  }

  /**
   * Resend an invitation.
   */
  async resendInvitation(tokenId: string): Promise<{ success: boolean; tokenId: string; message: string }> {
    const response = await apiClient.post<{ success: boolean; tokenId: string; message: string }>(
      `${this.basePath}/invitations/${tokenId}/resend`,
      {}
    );
    return response.data;
  }

  /**
   * Cancel an invitation.
   */
  async cancelInvitation(tokenId: string): Promise<void> {
    await apiClient.delete(`${this.basePath}/invitations/${tokenId}`);
  }
}

export const teamMembersService = new TeamMembersService();
